import {
	useState,
	useContext,
	useMemo,
	useEffect,
	useCallback,
	useRef,
} from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';

import config from '@/config';
import { Article, Issue as IssueModel, READING_MODE_KEY } from '@/constants';

import { contentOf, useScreenView } from '@shared/events';
import { useFont } from '@shared/lib/hooks';
import { focusMainContent } from '@shared/ui/skip-link';
import { AppContext } from '@/app.context';
import { ONLINE_ISSUE_BASE_PATH, type IssueContextProps } from '@entities/issue';
import type { ReaderStateContextProps } from '@widgets/issue-reader';

import {
	useDownloadAssets,
	useArticle,
	useLoad,
	useTitle,
	IssueSearch,
	useArticleLang,
	useStylesMap,
} from './issue.hooks';
import { ISSUE_UNAVAILABLE_TOAST_MS } from '../ui/issue-unavailable-toast';

/**
 * The issue-reading page's entire state machine, factored out (improvements/
 * 07.1-per-template-issue-reader-widgets.md, Phase 2 follow-up) so it is
 * genuinely shared, not merely copy-pasted, between `@pages/issue`'s own
 * `Issue` and a template's own issue page (e.g.
 * `templates/morningstar/issue/issue.tsx`). Per that document's guiding
 * rule: the *functionality* here is identical for every template -- loading
 * the issue, tracking the current article, language/font state,
 * the audio player hookup -- only the UI composing it (via
 * `ui/issue-reader-shell.tsx`, or a template's own bespoke composition) is
 * allowed to differ. Nothing here should become template-aware; a template
 * that needs different *behavior*, not just different UI, is a sign this
 * hook's contract needs to grow a parameter, not that the template should
 * fork the state logic.
 */
export function useIssueView(args: UseIssueViewArgs): UseIssueViewResult {
	const { onClickLogOut } = args;
	const { lang, baseUrl } = useContext(AppContext);
	const navigate = useNavigate();
	const router = useRouter();
	const [language, setLanguage] = useState<string>(lang);
	const [mode, setMode] = useState<ContentType>(getDefaultMode());
	const { fontSize, setFontSize } = useFont();
	const [audioPlayerUrl, setAudioPlayerUrl] = useState<string | null>(null);
	const [showAudioPlayer, setShowAudioPlayer] = useState(false);

	const { issue, isUnavailableOffline, currentArticle, setCurrentArticle } =
		useLoad();
	const languages = useLanguages(issue);
	const articleLanguages = useArticleLanguages(currentArticle);
	const disabledLanguages = diffSet(languages, articleLanguages);

	// Offline with nothing cached for this issue (useLoad/useIssue determine
	// this synchronously, before ever attempting to fetch): rather than
	// showing anything on this route, tell the user why and bounce back.
	// Prefer going back to wherever they came from (router history) over
	// always landing on home; only fall back to home when there's nowhere
	// to go back to (e.g. a deep link opened directly, offline, on a never-
	// visited issue).
	//
	// The redirect is delayed rather than immediate -- see
	// issue-unavailable-toast.tsx's doc comment for why: unlike the
	// `alert()` this replaced, a toast doesn't block, and the shell unmounts
	// (taking the toast with it) the moment navigate()/history.back() fires,
	// so firing it in the same tick would mean the message is never seen.
	useEffect(() => {
		if (!isUnavailableOffline) return;
		const timer = setTimeout(() => {
			if (router.history.canGoBack()) {
				router.history.back();
			} else {
				navigate({ to: baseUrl, replace: true });
			}
		}, ISSUE_UNAVAILABLE_TOAST_MS);
		return () => clearTimeout(timer);
	}, [isUnavailableOffline, navigate, baseUrl, router]);

	useEffect(() => {
		// Shared with Settings (Reader → Reading → Reading mode).
		localStorage.setItem(READING_MODE_KEY, mode);
	}, [mode]);

	// Audio is a responsive-reader feature: leaving it for replica (single or
	// double page) stops playback and resets the lock-screen session, which
	// `AudioPlayer` does whenever its url is cleared.
	useEffect(() => {
		if (mode !== 'responsive') {
			setAudioPlayerUrl(null);
		}
	}, [mode]);

	// Switching between the responsive and replica readers unmounts the view
	// the reader was in -- usually from its own menu, whose focused item goes
	// with it, leaving focus on <body>. The old view only unmounts once the
	// new (lazily loaded) one is ready, so this polls: as soon as focus has
	// fallen to <body>, move it to the new view's content. Focus on a real
	// element is never taken. Skipped on the first render.
	const previousMode = useRef(mode);
	useEffect(() => {
		if (previousMode.current === mode) {
			return;
		}
		previousMode.current = mode;
		let attempts = 0;
		const timer = setInterval(() => {
			attempts += 1;
			const focusFell =
				!document.activeElement || document.activeElement === document.body;
			if ((focusFell && focusMainContent()) || attempts >= 20) {
				clearInterval(timer);
			}
		}, 100);
		return () => clearInterval(timer);
	}, [mode]);

	useDownloadAssets({ issue: issue ?? null });

	useArticle({ article: currentArticle ?? null, issue });
	useArticleLang({
		article: currentArticle,
		languages,
		setLanguage,
	});

	const documentMeta = useTitle({ article: currentArticle, lang: language });
	useScreenView({
		enabled: !!currentArticle,
		content: contentOf({ issue, article: currentArticle, language }),
	});

	const stylesMap = useStylesMap(issue);
	const articlesMap: Map<string, Article> | undefined = useMemo(() => {
		if (!issue) {
			return undefined;
		}

		if (!issue.articles) {
			return undefined;
		}
		return issue.articles.reduce((acc: Map<string, Article>, article: Article) => {
			const { id } = article;
			acc.set(`${id}`, article);
			return acc;
		}, new Map<string, Article>());
	}, [issue]);

	const internalArticlesMap: Map<number, Article> = useMemo(() => {
		if (!issue) {
			return new Map();
		}

		if (!issue.articles) {
			return new Map();
		}
		const { articles } = issue;
		return articles.reduce(
			(acc: Map<number, Article>, article: Article) => {
				const { ArticleID } = article;
				acc.set(parseInt(`${ArticleID}`, 10), article);
				return acc;
			},
			new Map(),
		);
	}, [issue]);

	const navigateToArticle = useCallback(
		(articleId: number) => {
			const selectedArticle = internalArticlesMap.get(articleId);
			if (!selectedArticle) {
				return;
			}
			setCurrentArticle(selectedArticle);
		},
		[internalArticlesMap, setCurrentArticle],
	);

	const changeLanguage = useCallback(
		(lang: string) => {
			navigate({
				to: '.',
				search: (prevSearch: IssueSearch) => ({
					...prevSearch,
					lang,
				}),
			});
		},
		[navigate],
	);

	// Both context values are built above the two early-return conditions
	// this hook's callers check (`isUnavailableOffline`, then `!currentArticle
	// || !stylesMap || !issue`) -- hooks can't follow a conditional return,
	// and `IssueContextProps` already models `issue`/`currentArticle` as
	// nullable, so nothing is lost by always building this.
	const issueContextValue = useMemo(
		() => ({
			language,
			issue: issue ?? null,
			stylesMap: stylesMap ?? new Map(),
			currentArticle: currentArticle ?? null,
			setCurrentArticle,
			articles: issue?.articles ? issue.articles : [],
			articlesMap,
			navigateToArticle,
			languages,
			articleLanguages,
			disabledLanguages,
			setLanguage: changeLanguage,
			basePath: ONLINE_ISSUE_BASE_PATH,
		}),
		[
			language,
			issue,
			stylesMap,
			currentArticle,
			setCurrentArticle,
			articlesMap,
			navigateToArticle,
			languages,
			articleLanguages,
			disabledLanguages,
			changeLanguage,
		],
	);

	const readerStateValue = useMemo(
		() => ({
			setMode,
			fontSize,
			setFontSize,
			onClickLogOut,
			audioPlayerUrl,
			setAudioPlayerUrl,
			showAudioPlayer,
			setShowAudioPlayer,
		}),
		[
			fontSize,
			setFontSize,
			onClickLogOut,
			audioPlayerUrl,
			showAudioPlayer,
		],
	);

	return {
		documentMeta,
		issue,
		currentArticle,
		stylesMap,
		isUnavailableOffline,
		mode,
		issueContextValue,
		readerStateValue,
	};
}

export interface UseIssueViewArgs {
	onClickLogOut?: (url?: string) => void;
}

export interface UseIssueViewResult {
	documentMeta: ReturnType<typeof useTitle>;
	issue: IssueModel | null | undefined;
	currentArticle: Article | null | undefined;
	stylesMap: ReturnType<typeof useStylesMap>;
	isUnavailableOffline: boolean;
	mode: ContentType;
	issueContextValue: IssueContextProps;
	readerStateValue: ReaderStateContextProps;
}

export type ContentType = 'replica' | 'responsive' | 'responsive-replica';

function getDefaultMode(): ContentType {
	const { contentType } = config.issue;
	switch (contentType) {
		case 'replica':
		case 'responsive':
			return contentType;
		default:
			if (!localStorage.getItem(READING_MODE_KEY)) {
				localStorage.setItem(READING_MODE_KEY, 'responsive');
			}
			return localStorage.getItem(READING_MODE_KEY) as ContentType;
	}
}

function useLanguages(issue: IssueModel | null | undefined): Set<string> {
	const [languages, setLanguages] = useState(new Set<string>());
	useEffect(() => {
		if (!issue) {
			return;
		}

		if (!issue.articles || !issue.articles.length) {
			return;
		}

		const acc = new Set<string>();
		for (const article of issue.articles) {
			for (const lang in article.name) {
				acc.add(lang);
			}
		}
		setLanguages(acc);
	}, [issue]);

	return languages;
}

function useArticleLanguages(article: Article | null | undefined): Set<string> {
	const [languages, setLanguages] = useState(new Set<string>());
	useEffect(() => {
		if (!article) {
			return;
		}

		const acc = new Set<string>();
		for (const lang in article.name) {
			acc.add(lang);
		}

		setLanguages(acc);
	}, [article]);
	return languages;
}

function diffSet<T>(a: Set<T>, b: Set<T>): Set<T> {
	const diff = [...a].filter((i) => !b.has(i));
	return new Set(diff);
}
